import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { canvasKeys, startSession } from '../api/canvas-captures';

/**
 * The session this browse belongs to — created on the first capture, not on
 * arrival.
 *
 * A session is a set of screenshots, so a browser someone opened and closed
 * without capturing anything is not one: creating it on arrival filled the
 * dashboard with empty sessions and made the counts meaningless.
 *
 * `projectId` is the project it was started from (`?project=`). Absent, the
 * server puts the session in a new project named after the URL's host.
 *
 * The id is held in state and a ref rather than read off the mutation: a
 * remount (StrictMode in dev, or the auth gate resolving underneath) throws
 * mutation state away, and a session created but then forgotten would leave
 * the next capture starting a second one.
 */
export function useSession(
  url: string,
  sessionIdFromUrl: string | null,
  projectId: string | null,
) {
  const queryClient = useQueryClient();
  const [createdSessionId, setCreatedSessionId] = useState<string | null>(null);
  const sessionIdRef = useRef<string | null>(sessionIdFromUrl);
  sessionIdRef.current = sessionIdFromUrl ?? createdSessionId;

  const start = useMutation({
    mutationFn: () => startSession(projectId ? { projectId, url } : { url }),
    onSuccess: (result) => {
      setCreatedSessionId(result.session.id);
      // The board this walk opens reads it — seeded, so its title is right
      // from the first frame instead of changing under the handover.
      queryClient.setQueryData(
        canvasKeys.session(result.session.id),
        result.session,
      );
      void queryClient.invalidateQueries({ queryKey: canvasKeys.projects() });
      void queryClient.invalidateQueries({ queryKey: canvasKeys.rooms() });
      void queryClient.invalidateQueries({
        queryKey: canvasKeys.sessions(result.project.id),
      });
    },
  });

  const { mutateAsync: startSessionAsync } = start;

  /**
   * The session to put a capture in, creating it if this is the first.
   *
   * Awaited by the capture path, so a burst of captures can't each create a
   * session: the in-flight promise is shared until it resolves.
   */
  const pendingRef = useRef<Promise<string> | null>(null);

  const ensureSession = useCallback(async (): Promise<string> => {
    const existing = sessionIdRef.current;
    if (existing) return existing;

    pendingRef.current ??= startSessionAsync()
      .then((result) => {
        sessionIdRef.current = result.session.id;
        return result.session.id;
      })
      .finally(() => {
        pendingRef.current = null;
      });

    return pendingRef.current;
  }, [startSessionAsync]);

  return {
    ensureSession,
    error: start.error,
    /** The session, once one exists. Null until the first capture. */
    sessionId: sessionIdFromUrl ?? createdSessionId,
  };
}
